import React, { FC, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./components/ui/button";
import "./styles/globals.css";

const OrgEqPrivacy: FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Set page title
    document.title = "Privacy Policy - OrgEQ";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Privacy Policy for OrgEQ - Learn how Leadoff.ai, Inc. protects your privacy and organizational data in compliance with CCPA, GDPR, and SOC 2 standards.');
    }
  }, []);

  const formatSection = (content: string) => {
    const formatted: JSX.Element[] = [];
    
    // Remove "Privacy Policy" title from content
    let workingContent = content.replace(/^Privacy Policy\s*\n\s*/i, '');
    
    // Normalize line breaks - handle multiple cases of concatenated content
    
    // 1. Split items after colons without spaces: "through:Website" -> "through:\nWebsite"
    workingContent = workingContent.replace(/(:\s*)([A-Z][a-zA-Z\s]+:)/g, '$1\n$2');
    
    // 2. Split items separated by period+capital letter (key-value items): "description.Word:"
    workingContent = workingContent.replace(/(\.)([A-Z][a-zA-Z\s]+:)/g, '$1\n$2');
    
    // 3. Split sentences concatenated without spaces: "promptly.Customer" -> "promptly.\nCustomer"
    // Handle period followed by capital letter (general case)
    workingContent = workingContent.replace(/([a-z])(\.)([A-Z][a-z])/g, '$1$2\n$3');
    
    // 4. Split items after closing paren: "inquiries)Address:" -> "inquiries)\nAddress:"
    workingContent = workingContent.replace(/(\))([A-Z][a-zA-Z\s]+:)/g, '$1\n$2');
    
    // 5. Split key-value pairs that are on the same line: "Word1: desc.Word2: desc"
    // (handles cases where period+capital starts a new key-value item)
    workingContent = workingContent.replace(/([.!?])([A-Z][a-zA-Z\s]+:)/g, '$1\n$2');
    
    // Split by numbered sections - handle multi-digit numbers correctly
    // Find all section boundaries by matching section numbers at start of line
    const sectionMatches: Array<{index: number, number: string}> = [];
    const sectionPattern = /^(\d+)\.\s+/gm;
    let match;
    
    while ((match = sectionPattern.exec(workingContent)) !== null) {
      sectionMatches.push({
        index: match.index,
        number: match[1]
      });
    }
    
    // Split content at section boundaries
    const sections: string[] = [];
    for (let i = 0; i < sectionMatches.length; i++) {
      const startIndex = sectionMatches[i].index;
      const endIndex = i < sectionMatches.length - 1 ? sectionMatches[i + 1].index : workingContent.length;
      const sectionContent = workingContent.substring(startIndex, endIndex).trim();
      if (sectionContent) {
        sections.push(sectionContent);
      }
    }
    
    // If no sections found with the manual approach, fall back to regex split
    if (sections.length === 0) {
      const fallbackSections = workingContent.split(/(?=^\d+\.\s+)/m).map(s => s.trim()).filter(s => s);
      return formatSections(fallbackSections);
    }
    
    return formatSections(sections);
  };

  const formatSections = (sections: string[]) => {
    const formatted: JSX.Element[] = [];
    
    sections.forEach((section, sectionIdx) => {
      const lines = section.split('\n').filter(l => l.trim());
      let currentSectionContent: JSX.Element[] = [];
      let currentList: string[] = [];
      let inList = false;
      let expectingListAfterSubheader = false; // Track if we're expecting list items after a subsection header
      
      lines.forEach((line, lineIdx) => {
        const trimmed = line.trim();
        if (!trimmed) return;
        
        // Section header (e.g., "1. Introduction")
        if (/^\d+\.\s+/.test(trimmed) && lineIdx === 0) {
          // Close any open list from previous section
          if (inList && currentList.length > 0) {
            currentSectionContent.push(
              <ul key={`list-${sectionIdx}-${lineIdx}-pre`} className="list-disc ml-6 mb-4 space-y-2">
                {currentList.map((item, idx) => (
                  <li key={idx} className="text-gray-700">{item}</li>
                ))}
              </ul>
            );
            currentList = [];
            inList = false;
          }
          
          currentSectionContent.push(
            <h2 key={`header-${sectionIdx}`} className="text-2xl font-bold text-gray-900 mt-12 mb-6 first:mt-0">
              {trimmed}
            </h2>
          );
          return;
        }
        
        // Subsection header (ends with colon, not numbered, short line)
        if (trimmed.endsWith(':') && !trimmed.match(/^\d+\.|^[a-z]\)|^-/) && trimmed.length < 100 && !trimmed.includes('.')) {
          // Close any open list
          if (inList && currentList.length > 0) {
            currentSectionContent.push(
              <ul key={`list-${sectionIdx}-${lineIdx}`} className="list-disc ml-6 mb-4 space-y-2">
                {currentList.map((item, idx) => (
                  <li key={idx} className="text-gray-700">{item}</li>
                ))}
              </ul>
            );
            currentList = [];
            inList = false;
          }
          
          currentSectionContent.push(
            <h3 key={`subheader-${sectionIdx}-${lineIdx}`} className="text-xl font-semibold text-gray-800 mt-6 mb-3">
              {trimmed}
            </h3>
          );
          
          // Set flag that we're expecting list items after this header
          expectingListAfterSubheader = true;
          return;
        }
        
        // List items starting with dash or letter
        if (/^[-•]\s+/.test(trimmed) || /^[a-z]\)\s+/.test(trimmed)) {
          const cleanItem = trimmed.replace(/^[-•]\s*|[a-z]\)\s*/, '');
          if (cleanItem) {
            currentList.push(cleanItem);
            inList = true;
          }
          return;
        }
        
        // Key-value items (pattern: "Word: description")
        const keyValuePattern = /^[A-Z][a-zA-Z\s]+:/;
        
        // Check if this is a standalone key-value item on its own line (like "Essential Cookies: description")
        if (keyValuePattern.test(trimmed) && trimmed.includes(':')) {
          // Treat as list item if we're expecting a list after a subsection header, or already in a list
          if (expectingListAfterSubheader || inList) {
            currentList.push(trimmed);
            inList = true;
            expectingListAfterSubheader = false; // Reset flag since we're now in a list
            return;
          }
        }
        
        // Check if line contains multiple key-value items separated by period+capital (legacy format)
        if (keyValuePattern.test(trimmed) && trimmed.match(/\.\s*[A-Z]/)) {
          const items = trimmed.split(/(?<=\.)\s*(?=[A-Z][a-zA-Z\s]+:)/).filter(i => i.trim());
          
          if (items.length > 1) {
            items.forEach(item => {
              const cleanItem = item.trim();
              if (cleanItem) {
                currentList.push(cleanItem);
                inList = true;
              }
            });
            return;
          }
        }
        
        // Check if we should close the list before a regular paragraph
        // Close list if we encounter a regular paragraph (not a key-value item)
        if (inList && currentList.length > 0 && !keyValuePattern.test(trimmed)) {
          // If this is a regular paragraph (not continuing the list), close the list
          if (trimmed.length > 50 || trimmed.endsWith('.')) {
            currentSectionContent.push(
              <ul key={`list-${sectionIdx}-${lineIdx}`} className="list-disc ml-6 mb-4 space-y-2">
                {currentList.map((item, idx) => (
                  <li key={idx} className="text-gray-700">{item}</li>
                ))}
              </ul>
            );
            currentList = [];
            inList = false;
            expectingListAfterSubheader = false; // Reset flag
          } else if (trimmed.length < 150) {
            // Short line might continue the list
            currentList.push(trimmed);
            return;
          }
        }
        
        // Regular paragraph - only process if not already handled above
        // Reset expectingListAfterSubheader if we hit a regular paragraph
        if (!keyValuePattern.test(trimmed) && !inList) {
          expectingListAfterSubheader = false; // Reset flag when we see a regular paragraph
          // Split long paragraphs into sentences if needed
          const sentences = trimmed.split(/(?<=[.!?])\s+(?=[A-Z])/).filter(s => s.trim());
          
          sentences.forEach((sentence, sentIdx) => {
            const cleanSentence = sentence.trim();
            if (cleanSentence) {
              currentSectionContent.push(
                <p key={`para-${sectionIdx}-${lineIdx}-${sentIdx}`} className="mb-4 text-gray-700 leading-relaxed">
                  {cleanSentence}
                </p>
              );
            }
          });
        }
      });
      
      // Close any remaining list
      if (inList && currentList.length > 0) {
        currentSectionContent.push(
          <ul key={`list-${sectionIdx}-end`} className="list-disc ml-6 mb-4 space-y-2">
            {currentList.map((item, idx) => (
              <li key={idx} className="text-gray-700">{item}</li>
            ))}
          </ul>
        );
      }
      
      // Reset flags for next section
      expectingListAfterSubheader = false;
      
      formatted.push(...currentSectionContent);
    });
    
    return formatted;
  };

  const privacyContent = `Privacy Policy

1. Introduction

Leadoff.ai, Inc. ("we," "our," or "us") operates OrgEQ, an employee experience and sentiment analytics platform. We are committed to protecting the privacy of our users and their organizational data. This Privacy Policy outlines our practices regarding the collection, use, and sharing of personal information related to our OrgEQ platform. Our commitment extends to compliance with the California Consumer Privacy Act (CCPA), the General Data Protection Regulation (GDPR), and SOC 2 Type II security and availability standards.

2. Information Collection

Personal Information Collected

Identifiers: Such as name, email address, IP address, account information, and organizational role information.Organizational Data: Company name, employee information, organizational structure data, and team hierarchies provided by customer organizations.Employee Communication Data: Content from email messages, Slack/chat messages, and call transcripts that customer organizations choose to analyze through the OrgEQ platform.Analytics and Processing Data: Emotion analysis results, sentiment metrics, tracker evaluation data, aggregated organizational health insights, and trend analysis generated through our AI-powered analysis services.Financial Information: Payment details if purchasing services.Usage Data: Information on how services are used, including interaction data, feature usage, and access patterns.Integration Credentials: OAuth tokens, API keys, and configuration data necessary for third-party integrations (such as Gmail and Slack) as authorized by customer organizations.

Methods of Collection

Through direct interactions when customer organizations register, purchase services, configure integrations, or request support.Automatically via integrations when customer organizations authorize connections to email systems (Gmail) and communication platforms (Slack) for sentiment analysis services.Automatically via cookies and analytics when users visit our website or use our platform.Through AI processing when customer organizations submit communication content for emotion and sentiment analysis.

3. Use of Information

We use the collected data to:Provide and improve our services, including emotion detection, sentiment analysis, organizational health metrics, and employee experience insights through the OrgEQ platform.Process and analyze communications as authorized by customer organizations to generate sentiment trends, emotion patterns, and organizational health insights.Communicate with you regarding your account, services, important updates, or support requests.Comply with legal obligations and protect our legal rights, including compliance with data protection regulations and SOC 2 security standards.Conduct analytics and business intelligence to improve our platform functionality and service delivery.Maintain system security through access controls, authentication, and monitoring as required by our SOC 2 compliance framework.

4. Data Sharing and Disclosure

Third Parties

Service Providers: We share data with trusted service providers who support our service delivery under strict confidentiality agreements, including:

Cloud hosting providers (Netlify, Railway) for application hosting

Database service providers (MongoDB Atlas) for data storage

AI/ML service providers (Hume AI) for emotion and sentiment analysis

Payment processors for transaction processing

All service providers are required to maintain SOC 2 compliant security practices or equivalent standards

Integration Providers: When customer organizations authorize integrations (such as Gmail or Slack), data sharing is governed by the terms and privacy policies of those third-party services. We only access data explicitly authorized by customer organizations.Legal Requirements: When required by law, court order, or to protect our legal rights and the rights of our users.Business Transactions: In the event of a merger, acquisition, or sale of assets, user data may be transferred as part of the transaction, with appropriate notice to affected parties.

Disclosure Practices

We do not sell personal data as defined under CCPA. We do not sell, rent, or trade employee communication data or personal information to third parties for marketing or other commercial purposes.Users will be notified if their data is subject to any business transactions or legal disclosures, except where prohibited by law.SOC 2 Compliance: All data sharing and disclosure practices are conducted in accordance with our SOC 2 Type II controls and security commitments.

5. Data Security

We implement security measures aligned with SOC 2 Type II standards to protect your data, including:Encryption: Data encryption both in transit (TLS 1.2+) and at rest for all customer data and communications.Access Controls: Role-based access control (RBAC) systems ensuring that access to personal information is restricted to authorized personnel based on job function and need-to-know principles.Secure Infrastructure: Production systems hosted on SOC 2 compliant cloud providers (Netlify, Railway, MongoDB Atlas) with physical and logical security protections.Vulnerability Management: Regular vulnerability scans, penetration testing, and security assessments of our production environment.Incident Response: Documented procedures for identifying, reporting, and responding to security incidents, including breach notification procedures.Data Retention and Disposal: Policies and procedures for secure data retention and disposal in accordance with legal requirements and customer agreements.Monitoring and Logging: Continuous monitoring of systems, networks, and access to detect and respond to security threats.Change Management: Documented Systems Development Life Cycle (SDLC) procedures with security controls for all application and infrastructure changes.In the event of a data breach, affected individuals and customer organizations will be notified in accordance with legal requirements and our security commitments, typically within 72 hours of discovery as required by applicable regulations.

6. User Rights

CCPA and GDPR Rights

Access and Portability: Request access to and copies of your personal data, including the right to receive your data in a structured, commonly used format.Correction: Update or correct inaccuracies in your personal information.Deletion: Request deletion of your personal data, subject to legal retention requirements and our SOC 2 data retention policies.Objection and Restriction: Object to processing of your personal data and request restriction of processing in certain circumstances.Consent Withdrawal: Withdraw consent where applicable, including the ability to revoke authorization for third-party integrations.Opt-Out of Sale: As stated above, we do not sell personal data. If this practice changes, you will have the right to opt-out under CCPA.Non-Discrimination: We will not discriminate against you for exercising your privacy rights.

Exercising Your Rights

To exercise these rights, please contact us via it@leadoff.ai. We will respond to your request within the timeframes required by applicable law (typically 30 days under CCPA, one month under GDPR). We may require verification of your identity before processing certain requests to protect your privacy and security.For customer organizations managing employee data, please note that organizational administrators may need to coordinate requests related to employee communication data that has been processed through the OrgEQ platform.

7. International Data Transfers

Data collected through the OrgEQ platform may be transferred and stored outside your jurisdiction, including in the United States. We ensure all international transfers are protected by appropriate legal safeguards, including:Standard Contractual Clauses (SCCs): Where applicable, we use EU Standard Contractual Clauses approved by the European Commission to ensure adequate protection of personal data transferred outside the EEA.Adequacy Decisions: We rely on adequacy decisions by the European Commission where applicable.Service Provider Compliance: All international service providers are contractually required to maintain appropriate data protection standards equivalent to SOC 2 Type II or higher.Data Location: While we strive to store data in regions preferred by customer organizations, technical and operational requirements may necessitate data storage in multiple geographic locations. Customer organizations will be informed of primary data storage locations in their service agreements.

8. Cookies and Tracking Technologies

We use cookies and similar tracking technologies to enhance site functionality, improve user experience, and analyze platform usage. These technologies include:

Essential Cookies: Required for the platform to function properly, including authentication and security features.

Analytics Cookies: Used to understand how users interact with our platform to improve our services.

Functional Cookies: Used to remember your preferences and settings.

You can manage cookie preferences through your browser settings. Note that disabling certain cookies may limit functionality of the OrgEQ platform.

We do not use cookies for advertising purposes or to track users across third-party websites.

9. Children's Privacy

Our services are not directed at children under the age of 18. We do not knowingly collect personal data from children without parental consent. If we become aware that we have collected personal information from a child under 18 without appropriate consent, we will take steps to delete such information promptly.Customer organizations using OrgEQ are responsible for ensuring compliance with applicable laws regarding the collection and processing of data from minors, and should configure their organizational settings accordingly.

10. Updates to the Privacy Policy

This policy may be updated periodically to reflect changes in our practices, legal requirements, or platform functionality. We will notify users of significant changes through:Website Notification: Prominent notice on our website when material changes are made.Direct Communication: Email notification to registered users for substantial changes affecting their data rights or our data practices.Policy Versioning: The "Last Updated" date at the top of this policy will reflect the most recent revision date.Your continued use of the OrgEQ platform after changes become effective constitutes acceptance of the updated Privacy Policy. If you do not agree with the changes, you may discontinue use of the services or contact us to discuss your concerns.We recommend reviewing this Privacy Policy periodically to stay informed about how we protect your information.

11. Contact Information

For any privacy-related questions, concerns, or to exercise your privacy rights, please contact us at:Email: support@leadoff.ai or it@leadoff.ai (for data protection inquiries)Address: Leadoff.ai, Inc.
7943 Ramsgate Avenue
Los Angeles, CA 90045`;

  return (
    <div className="org-eq-dashboard min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <svg className="absolute w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="pulseGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#06b6d4">
                <animate
                  attributeName="stop-color"
                  values="#06b6d4; #14b8a6; #10b981; #06b6d4"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="50%" stopColor="#14b8a6">
                <animate
                  attributeName="stop-color"
                  values="#14b8a6; #10b981; #06b6d4; #14b8a6"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="100%" stopColor="#10b981">
                <animate
                  attributeName="stop-color"
                  values="#10b981; #06b6d4; #14b8a6; #10b981"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>
          </defs>
          <path
            d="M-200 50 L-150 50 L-100 50 L-80 30 L-60 70 L-40 50 L0 50 L50 50 L100 50 L120 30 L140 70 L160 50 L200 50 L250 50 L300 50 L320 30 L340 70 L360 50 L400 50 L450 50 L500 50 L520 30 L540 70 L560 50 L600 50 L650 50 L700 50 L720 30 L740 70 L760 50 L800 50 L850 50 L900 50 L920 30 L940 70 L960 50 L1000 50 L1050 50 L1100 50 L1120 30 L1140 70 L1160 50 L1200 50 L1250 50 L1300 50 L1320 30 L1340 70 L1360 50 L1400 50 L1450 50 L1500 50 L1520 30 L1540 70 L1560 50 L1600 50 L1650 50 L1700 50 L1720 30 L1740 70 L1760 50 L1800 50 L1850 50 L1900 50 L1920 30 L1940 70 L1960 50 L2000 50"
            stroke="url(#pulseGradient)"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <animateTransform
              attributeName="transform"
              type="translate"
              from="0 0"
              to="400 0"
              dur="4s"
              repeatCount="indefinite"
            />
          </path>
        </svg>
      </div>

      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-3">
            <img src="/logos/orgeq-logo.svg" alt="OrgEQ" className="h-14 w-auto" />
          </button>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate("/org-eq/login")}>
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 lg:py-16 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border p-8 lg:p-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
            
            <div className="prose prose-lg max-w-none">
              {formatSection(privacyContent)}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OrgEqPrivacy;

